import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Link,
  Modal,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Tag,
} from '@carbon/react';
import { DocumentView, Flag, ViewOff } from '@carbon/react/icons';
import request from 'lib/request';
import { useEntityDocuments } from './use-entity-documents';
import styles from './reconcile-entity-documents.module.scss';

interface ReconcileEntityDocumentsProps {
  entityId: number;
  // The entity's display name — rendered in the Entity name column so the table
  // is self-describing (§3.13). Optional so the legacy row-expansion drilldown,
  // which doesn't thread a name, keeps working (falls back to a dash).
  entityName?: string | null;
  // The entity's address, shown per document row. The drilldown is scoped to a
  // single entity and the documents query carries no per-row address, so the
  // entity's address stands in (matching the mock).
  address?: string | null;
  // The entity's per-address list (id/label + flag), threaded from the candidate
  // row so the Address column renders one address per line with the same
  // highlight + Review badge as the candidate table. Optional: the legacy
  // row-expansion drilldown threads only the joined `address` string.
  addresses?: EntityAddress[];
  // §3.13 row-click → highlight the source candidate: clicking a document row
  // emits this entity's id so the host can light up its row in the candidate
  // table. Optional — omitting it leaves rows non-interactive (the drilldown).
  onSelectEntity?: (entityId: number) => void;
  // The currently-highlighted entity id (owned by the host), so every row of the
  // highlighted entity's table reads as selected.
  selectedEntityId?: number | null;
  // Fired after an address-confidence flag is cleared for this entity (#26), so
  // the host can optimistically drop the row/name flag styling + submit block.
  onFlagCleared?: (entityId: number) => void;
  // View-document toggle (mock v25): when provided, the row's View button opens
  // the document in the shared inline Document viewer pane instead of a new tab.
  onViewDocument?: (documentId: number, documentName?: string) => void;
  viewedDocumentId?: number | null;
}

// Renders a Rails date string ("YYYY-MM-DD…") as M/D/YYYY without a timezone
// shift (date-only values must not be passed through Date()).
export function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return match ? `${Number(match[2])}/${Number(match[3])}/${match[1]}` : iso;
}

// One address per line, matching the candidate table: a flagged address is
// highlighted and carries an inline "Review" badge with its criticality.
type EntityAddress = {
  id: number;
  label: string;
  flagged?: boolean;
  criticality?: 'high' | 'medium' | null;
};

const highlightClassFor = (criticality?: 'high' | 'medium' | null) => {
  return criticality === 'high'
    ? styles.flagHighlightHigh
    : styles.flagHighlightMed;
};

const ReviewTag: React.FC<{ criticality?: 'high' | 'medium' | null }> = ({
  criticality,
}) => (
  <Tag type={criticality === 'high' ? 'red' : 'warm-gray'} size="sm">
    Review: {criticality === 'high' ? 'High' : 'Med'}
  </Tag>
);

// The entity's address stand-in for a non-flagged document row. Prefer the
// per-address list (threaded from the candidate row) so a multi-address entity
// renders one address per line with the same highlight + Review badge as the
// candidate table. Falls back to splitting the pre-joined "; " string for legacy
// callers that thread only the joined address.
const EntityAddressList: React.FC<{
  addresses?: EntityAddress[];
  address?: string | null;
}> = ({ addresses, address }) => {
  if (addresses && addresses.length > 0) {
    return (
      <span className={styles.addressList}>
        {addresses.map((a) => (
          <span key={a.id} className={styles.addressItem}>
            {a.flagged ? (
              <>
                <span className={highlightClassFor(a.criticality)}>
                  {a.label}
                </span>
                <ReviewTag criticality={a.criticality} />
              </>
            ) : (
              a.label
            )}
          </span>
        ))}
      </span>
    );
  }
  const lines = address?.split('; ').filter(Boolean) ?? [];
  if (lines.length === 0) return <>—</>;
  return (
    <span className={styles.addressList}>
      {lines.map((line, i) => (
        <span key={i} className={styles.addressItem}>
          {line}
        </span>
      ))}
    </span>
  );
};

type SortKey = 'doc' | 'docType' | 'recordedDate';

const PAGE_SIZE = 10;

// Row-expansion / §3.13 drilldown content: the documents that cite this entity,
// as a table (Document · Document type · Recording date + Effective date ·
// Address · Entity ID · Entity name), so the examiner can see where a candidate
// came from before reconciling. Recording date pages come from the server
// (`page`/`page_size`); the in-table search + column sort refine the loaded page
// client-side.
const ReconcileEntityDocuments: React.FC<ReconcileEntityDocumentsProps> = ({
  entityId,
  entityName,
  address,
  addresses,
  onSelectEntity,
  selectedEntityId,
  onFlagCleared,
  onViewDocument,
  viewedDocumentId,
}) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  // The document row whose address flag the user is confirming a clear on (#26).
  const [clearTarget, setClearTarget] = useState<{
    documentId: number;
    addressId: number;
    label: string;
  } | null>(null);
  const [clearing, setClearing] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);

  const { documents, totalCount, loading, loaded, error, fetchDocuments } =
    useEntityDocuments(entityId, page, PAGE_SIZE);

  useEffect(() => {
    void fetchDocuments();
  }, [fetchDocuments]);

  const handleClearFlag = async () => {
    if (!clearTarget) return;
    setClearing(true);
    setClearError(null);
    const response = await request.post<
      { addressId: number },
      { error: string }
    >(
      `/documents/${clearTarget.documentId}/structured_addresses/${clearTarget.addressId}/clear_flag`,
      {},
      new AbortController()
    );
    setClearing(false);
    if (response.type === 'success') {
      setClearTarget(null);
      onFlagCleared?.(entityId);
      // Re-fetch so the cleared row loses its Clear-flag action.
      void fetchDocuments();
    } else {
      const message =
        response.type === 'error' ? response.data?.error : response.message;
      setClearError(message ?? 'Could not clear the flag.');
    }
  };

  const isSelected = selectedEntityId === entityId;
  const interactive = Boolean(onSelectEntity);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? documents.filter((doc) =>
          [
            doc.documentTitle,
            doc.documentType,
            doc.recordedDate,
            doc.effectiveDate,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
            .includes(q)
        )
      : documents;
    if (!sortKey) return filtered;
    const dir = sortDir === 'asc' ? 1 : -1;
    const value = (doc: (typeof documents)[number]): string => {
      if (sortKey === 'doc')
        return doc.documentTitle ?? doc.documentType ?? `Document ${doc.id}`;
      if (sortKey === 'docType') return doc.documentType ?? '';
      return doc.recordedDate ?? '';
    };
    return [...filtered].sort(
      (a, b) =>
        value(a).localeCompare(value(b), undefined, { numeric: true }) * dir
    );
  }, [documents, search, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };
  const sortDirection = (key: SortKey) => {
    if (sortKey !== key) return 'NONE';
    return sortDir === 'asc' ? 'ASC' : 'DESC';
  };

  if (loading && !loaded) {
    return <p className={styles.hint}>Loading documents…</p>;
  }
  // A load failure (network / server) must read distinctly from an empty result
  // — otherwise the table's "No documents match this search." copy would masquerade
  // as a genuinely empty set.
  if (error && !loaded) {
    return (
      <p className={styles.hint} role="alert">
        {error}
      </p>
    );
  }
  if (loaded && documents.length === 0) {
    return <p className={styles.hint}>No linked documents.</p>;
  }

  return (
    <div className={styles.shell}>
      <TableToolbar size="sm">
        <TableToolbarContent>
          <TableToolbarSearch
            persistent
            placeholder="Search documents"
            labelText="Search documents"
            onChange={(_evt, value?: string) => setSearch(value ?? '')}
          />
        </TableToolbarContent>
      </TableToolbar>
      <Table size="sm" className={styles.table}>
        <TableHead>
          <TableRow>
            {/* View / Clear-flag actions — no header label (mock v25). */}
            <TableHeader
              className={styles.actionsHeader}
              aria-label="Actions"
            />
            <TableHeader
              isSortable
              isSortHeader={sortKey === 'doc'}
              sortDirection={sortDirection('doc')}
              onClick={() => handleSort('doc')}
            >
              Document
            </TableHeader>
            <TableHeader
              isSortable
              isSortHeader={sortKey === 'docType'}
              sortDirection={sortDirection('docType')}
              onClick={() => handleSort('docType')}
            >
              Document type
            </TableHeader>
            <TableHeader
              isSortable
              isSortHeader={sortKey === 'recordedDate'}
              sortDirection={sortDirection('recordedDate')}
              onClick={() => handleSort('recordedDate')}
            >
              Recording date
            </TableHeader>
            <TableHeader>Address</TableHeader>
            <TableHeader>Entity ID</TableHeader>
            <TableHeader>Entity name</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className={styles.emptyCell}>
                No documents match this search.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((doc) => {
              const rowProps = interactive
                ? {
                    'className': `${styles.row} ${
                      isSelected ? styles.rowHighlighted : ''
                    }`.trim(),
                    'aria-selected': isSelected || undefined,
                    'tabIndex': 0,
                    'onClick': () => onSelectEntity?.(entityId),
                    'onKeyDown': (e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelectEntity?.(entityId);
                      }
                    },
                  }
                : {};
              return (
                <TableRow key={doc.id} {...rowProps}>
                  <TableCell className={styles.actionsCell}>
                    {onViewDocument ? (
                      <Button
                        kind="ghost"
                        size="sm"
                        hasIconOnly
                        renderIcon={
                          viewedDocumentId === doc.id ? ViewOff : DocumentView
                        }
                        iconDescription={
                          viewedDocumentId === doc.id
                            ? 'Hide document'
                            : 'View document'
                        }
                        tooltipPosition="right"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          onViewDocument(
                            doc.id,
                            doc.documentTitle ?? doc.documentType ?? undefined
                          );
                        }}
                      />
                    ) : (
                      <Button
                        kind="ghost"
                        size="sm"
                        hasIconOnly
                        renderIcon={DocumentView}
                        iconDescription="View document"
                        tooltipPosition="right"
                        href={doc.href}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      />
                    )}
                    {doc.flaggedAddress && (
                      <Button
                        kind="ghost"
                        size="sm"
                        hasIconOnly
                        renderIcon={Flag}
                        iconDescription="Clear address-confidence flag"
                        tooltipPosition="right"
                        className={styles.clearFlagBtn}
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          setClearError(null);
                          setClearTarget({
                            documentId: doc.id,
                            addressId: doc.flaggedAddress!.id,
                            label: doc.flaggedAddress!.label,
                          });
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Link
                      href={doc.href}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                      {doc.documentTitle ??
                        doc.documentType ??
                        `Document ${doc.id}`}
                    </Link>
                  </TableCell>
                  <TableCell>{doc.documentType ?? '—'}</TableCell>
                  <TableCell>
                    <div className={styles.dates}>
                      <span>{formatDate(doc.recordedDate)}</span>
                      <span className={styles.effectiveDate}>
                        Effective: {formatDate(doc.effectiveDate)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {doc.flaggedAddress ? (
                      <span className={styles.flaggedAddress}>
                        <span
                          className={highlightClassFor(
                            doc.flaggedAddress.criticality
                          )}
                        >
                          {doc.flaggedAddress.label}
                        </span>
                        <ReviewTag
                          criticality={doc.flaggedAddress.criticality}
                        />
                      </span>
                    ) : (
                      <EntityAddressList
                        addresses={addresses}
                        address={address}
                      />
                    )}
                  </TableCell>
                  <TableCell>{entityId}</TableCell>
                  <TableCell>{entityName ?? '—'}</TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      {totalCount > PAGE_SIZE && (
        <Pagination
          size="sm"
          page={page}
          pageSize={PAGE_SIZE}
          pageSizes={[PAGE_SIZE]}
          totalItems={totalCount}
          onChange={({ page: nextPage }: { page: number }) => setPage(nextPage)}
        />
      )}
      {clearTarget && (
        <Modal
          open
          danger
          size="sm"
          modalHeading="Clear flag"
          primaryButtonText={clearing ? 'Clearing…' : 'Clear flag'}
          secondaryButtonText="Cancel"
          primaryButtonDisabled={clearing}
          onRequestClose={() => !clearing && setClearTarget(null)}
          onRequestSubmit={handleClearFlag}
        >
          <p>
            Clearing this flag removes the review indicator for{' '}
            <strong>{clearTarget.label}</strong> and accepts the address as-is.
          </p>
          <p>
            <strong>This action cannot be undone.</strong> A cleared flag can
            only be restored by re-running the agent.
          </p>
          {clearError && (
            <p className={styles.clearError} role="alert">
              {clearError}
            </p>
          )}
        </Modal>
      )}
    </div>
  );
};

export default ReconcileEntityDocuments;
